import { APP_TIME_ZONE } from './constants';

/**
 * עזרי זמן בשעון ישראל — משותפים לשרת ולממשק.
 * תאריך עבודה נשמר כ-YYYY-MM-DD ושעה כ-HH:mm, שניהם בשעון ישראל.
 */

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

export function isValidDateString(date: string): boolean {
  const m = DATE_RE.exec(date);
  if (!m) return false;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.getUTCFullYear() === Number(m[1]) && d.getUTCMonth() === Number(m[2]) - 1 && d.getUTCDate() === Number(m[3]);
}

export function isValidTimeString(time: string): boolean {
  return TIME_RE.test(time);
}

/** התאריך בישראל ברגע נתון, כ-YYYY-MM-DD. */
export function israelDate(at: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: APP_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).format(at);
}

export function shiftDate(date: string, days: number): string {
  const m = DATE_RE.exec(date);
  if (!m) throw new Error(`Invalid date: ${date}`);
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days));
  return d.toISOString().slice(0, 10);
}

/** ההפרש בדקות בין שעון ישראל ל-UTC ברגע נתון (180 בקיץ, 120 בחורף). */
export function israelOffsetMinutes(at: Date): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: APP_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(at);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'));
  return Math.round((asUtc - Math.floor(at.getTime() / 60000) * 60000) / 60000);
}

/**
 * הרגע (UTC) שבו בישראל מתחיל התאריך והשעה הנתונים.
 * מתמודד עם מעבר שעון קיץ/חורף.
 */
export function israelDateTimeToUtc(date: string, time = '00:00'): Date {
  const m = DATE_RE.exec(date);
  const t = TIME_RE.exec(time);
  if (!m || !t) throw new Error(`Invalid date/time: ${date} ${time}`);
  const naive = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(t[1]), Number(t[2]));
  // שני סבבים מספיקים כדי להתייצב גם סביב מעבר שעון
  let guess = naive - israelOffsetMinutes(new Date(naive)) * 60000;
  guess = naive - israelOffsetMinutes(new Date(guess)) * 60000;
  return new Date(guess);
}

/** "2026-10-22T18:30" (קלט datetime-local) בשעון ישראל → Date, או null. */
export function parseIsraelLocalDateTime(value: string): Date | null {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})$/.exec(value);
  if (!m || !isValidDateString(m[1]!) || !isValidTimeString(m[2]!)) return null;
  return israelDateTimeToUtc(m[1]!, m[2]!);
}

/** Date → "2026-10-22T18:30" בשעון ישראל, לשדה datetime-local. */
export function toIsraelLocalDateTime(at: Date): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: APP_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(at);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour') === '24' ? '00' : get('hour')}:${get('minute')}`;
}

/** "22.10.2026 18:30" לתצוגה. */
export function formatIsraelDateTime(at: Date): string {
  const v = toIsraelLocalDateTime(at);
  return `${v.slice(8, 10)}.${v.slice(5, 7)}.${v.slice(0, 4)} ${v.slice(11)}`;
}
